import React from 'react';
import { toast } from 'sonner';
import { Download, Loader2 } from 'lucide-react';
import { adminApi, getAdminUser } from '../../../lib/adminAuth';
import { useStickyState } from '../../../lib/useStickyState';
import { getTodayLocal } from '../../../lib/dateUtils';
import POAnalyticsFilters, { EMPTY_FILTERS } from './POAnalyticsFilters';
import POKpiStrip from './POKpiStrip';
import POHeatmap from './POHeatmap';
import PORankPanel from './PORankPanel';
import POTrendChart from './POTrendChart';
import POVendorTable from './POVendorTable';
import POProfitTable from './POProfitTable';
import PODrilldownSheet from './PODrilldownSheet';

const toParams = (f) => {
  const p = {};
  if (f.from) p.date_from = f.from;
  if (f.to) p.date_to = f.to;
  if (f.clients.length) p.clients = f.clients.join(',');
  if (f.categories.length) p.categories = f.categories.join(',');
  if (f.vendors.length) p.vendors = f.vendors.join(',');
  if (f.payment.length) p.payment = f.payment.join(',');
  if (f.approval.length) p.approval = f.approval.join(',');
  if (f.min !== '') p.min_amount = f.min;
  if (f.max !== '') p.max_amount = f.max;
  return p;
};

export default function POAnalytics() {
  const [filters, setFilters] = useStickyState('accounting.poAnalyticsFilters', EMPTY_FILTERS, getAdminUser()?.email || 'default');
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [drill, setDrill] = React.useState(null);
  const [exporting, setExporting] = React.useState(false);
  const params = React.useMemo(() => toParams({ ...EMPTY_FILTERS, ...filters }), [filters]);

  React.useEffect(() => {
    let live = true;
    setLoading(true);
    const t = setTimeout(() => {
      adminApi.get('/api/admin/accounting/po-analytics', { params })
        .then((r) => live && setData(r.data))
        .catch((e) => live && toast.error(e?.response?.data?.detail || 'Could not load PO analytics'))
        .finally(() => live && setLoading(false));
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [params]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await adminApi.get('/api/admin/accounting/po-analytics/export.csv', { params, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = `po-analytics-${getTodayLocal()}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch { toast.error('Export failed'); } finally { setExporting(false); }
  };

  const applyDrill = (d) => {
    setFilters((f) => ({
      ...f,
      clients: d.client ? [d.client] : f.clients,
      categories: d.category ? [d.category] : f.categories,
      vendors: d.vendor ? [d.vendor] : f.vendors,
    }));
    setDrill(null);
  };

  return (
    <div className="space-y-0" data-testid="po-analytics-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-bold text-[#1a1a2e] flex items-center gap-2">
            PO Analytics {loading && <Loader2 className="w-4 h-4 animate-spin text-fuchsia-600" data-testid="po-analytics-loading" />}
          </h2>
          <p className="text-xs text-slate-500">Cross-reference purchase orders by client, category, vendor and time</p>
        </div>
        <button type="button" onClick={exportCsv} disabled={exporting}
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#1a1a2e] text-white text-sm font-semibold hover:bg-fuchsia-700 transition-colors active:scale-95 disabled:opacity-60"
          data-testid="po-export-csv-button">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Export CSV
        </button>
      </div>
      <POAnalyticsFilters filters={{ ...EMPTY_FILTERS, ...filters }} setFilters={setFilters} options={data?.options} />
      {data && (
        <div className={`transition-opacity duration-200 ${loading ? 'opacity-60' : 'opacity-100'}`}>
          <POKpiStrip summary={data.summary} />
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 sm:gap-6 mb-6">
            <div className="xl:col-span-8 min-w-0"><POHeatmap matrix={data.matrix} onDrill={setDrill} /></div>
            <div className="xl:col-span-4 min-w-0"><PORankPanel byClient={data.by_client} byCategory={data.by_category} onDrill={setDrill} /></div>
          </div>
          <div className="mb-6"><POTrendChart trend={data.trend} onDrill={setDrill} /></div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
            <POVendorTable vendors={data.by_vendor} onDrill={setDrill} />
            <POProfitTable rows={data.rows} />
          </div>
        </div>
      )}
      <PODrilldownSheet drill={drill} rows={data?.rows} onClose={() => setDrill(null)} onApply={applyDrill} />
    </div>
  );
}
